'use client'

import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { IApi } from '@/setup/api-client'
import { useApiQuery } from '@/utils/api/api-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type { OrganizationDirectoryRequestBody } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/nodes/node'

type IGetNodeQueryParams = {
    organizationId: string
    brandSlug: string
} & OrganizationDirectoryRequestBody

export class GetNodeQuery {
    static getKey(params: IGetNodeQueryParams) {
        return [...NODES_BASE_KEY, params.organizationId, params.brandSlug, params.path, 'get-node-query']
    }

    static async call(api: IApi, { organizationId, brandSlug, path }: IGetNodeQueryParams) {
        try {
            const response = await api.post<INode, OrganizationDirectoryRequestBody>(
                `/organization/${organizationId}/brands/${brandSlug}/nodes/node`,
                { path }
            )
            return response?.data
        } catch {
            throw new Error('NODE_NOT_FOUND')
        }
    }
}

export const useGetNodeQuery = ({
    nodePath,
    enabled = true,
    onNotFound,
}: {
    nodePath: string
    enabled?: boolean
    onNotFound?: () => void
}) => {
    const { api, apiParams } = useApiToolbox()

    return useApiQuery({
        queryKey: GetNodeQuery.getKey({ ...apiParams, path: nodePath }),
        queryFn: async () => {
            try {
                return GetNodeQuery.call(api, { ...apiParams, path: nodePath })
            } catch (error) {
                if (error instanceof Error && error.message === 'NODE_NOT_FOUND') {
                    onNotFound?.()
                }
            }
        },
        enabled: !!apiParams.organizationId && !!nodePath && enabled,
    })
}
